// Dependencies
// -----------------------------------------------
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import SignaturePad from 'react-signature-pad-wrapper';
import styled from 'styled-components';
import { toast } from 'react-toastify';

// Components
// -----------------------------------------------
import {
  Box,
  CallToActionButton,
  Divider,
  FlexBox,
  TextDense
} from '@directsoftware/ui-kit-web-admin';
import Ripple from '../miscellaneous/ripple';
import { Delete } from '../listings/resources/icons';
import displayError from '../errors/display';
import { postWlsSignature, deleteWlsSignature } from '../../utils/wls-api-client';

// Styles
// -----------------------------------------------
const SignatureContainer = styled.div`
  border: 1px solid #e5e2e2;
  flex: 1 1 auto;
`;

const SignatureButtonContainer = styled.div`
  width: 100%;

  p {
    border-top: 1px solid #e5e2e2;
    margin: 0 8px 4px;
    text-align: center;
  }

  a.button {
    width: 50%;
    margin: 0;
  }
`;

// -----------------------------------------------
// COMPONENT->SIGNATURE-CAPTURE ------------------
// -----------------------------------------------
class SignatureCaptureV2 extends React.Component {
  // Constructor
  // ---------------------------------------------
  constructor(props) {
    super(props);

    this.state = {
      signature: props.signature || null,
      saving: false,
      width: 446,
      height: 150
    };
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.signature && this.props.signature && !this.state.signature) {
      this.setState({ signature: this.props.signature });
    }
  }

  // Clear Signature
  // ---------------------------------------------
  clearSignature = () => {
    this.signaturePad.clear();
  };

  // Delete Signature
  // ---------------------------------------------
  deleteSignature = () => {
    if (this.props.localOnly) {
      this.setState({ signature: null }, () => {
        this.props.afterDelete && this.props.afterDelete();
      });
      return;
    }
    deleteWlsSignature(this.props.brand, this.state.signature.id, this.props.booking.booking_code)
      .then(response => {
        this.setState({ signature: null }, () => {
          this.props.afterDelete && this.props.afterDelete();
        });
      })
      .catch(error => {
        displayError({ message: 'Signature could not be deleted', error });
      });
  };

  // Save Signature
  // ---------------------------------------------
  saveSignature = () => {
    if (this.signaturePad.isEmpty()) {
      toast.error('Please provide a signature.');
      return;
    }

    const signature = this.signaturePad.toDataURL();
    if (this.props.localOnly) {
      const savedSignature = { url: signature };
      this.setState({ signature: savedSignature }, () => {
        this.props.afterSave && this.props.afterSave(savedSignature);
      });
      return;
    }
    const sigData = {
      data_uri: signature,
      booking_code: this.props.booking.booking_code,
      booking_id: this.props.booking.id,
      organization_id: this.props.booking.organization_id
    };
    this.setState({ saving: true }, () => {
      postWlsSignature(this.props.brand, sigData)
        .then(response => {
          const savedSignature = {
            ...response.data.signature,
            url: (response.data.signature && response.data.signature.url) || signature
          };
          this.setState(
            { saving: false, signature: savedSignature },
            () => {
              this.props.afterSave && this.props.afterSave(savedSignature);
            }
          );
        })
        .catch(error => {
          displayError({ message: 'Error creating signature', error });
        });
    });
  };

  // Render Edit
  // ---------------------------------------------
  // Measure will calculate the dimensions of the
  // child element on initial load and whenever it
  // is resized
  renderEdit = () => (
    <>
      <SignaturePad
        height={208}
        options={{ backgroundColor: '#f4f4f9' }}
        ref={ref => (this.signaturePad = ref)}
      />
      <Box paddingVertical="xs">
        <TextDense isFullWidth textAlign="center">
          Sign Above
        </TextDense>
      </Box>
      <Divider />
      <FlexBox gap="s">
        <Box flex="1">
          <CallToActionButton onClick={() => this.saveSignature()} isFullWidth>
            Save
          </CallToActionButton>
        </Box>
        <Box flex="1">
          <CallToActionButton
            onClick={() => this.clearSignature()}
            variation="secondary"
            appearance="ghost"
            isFullWidth
          >
            Clear
          </CallToActionButton>
        </Box>
      </FlexBox>
    </>
  );

  // Render View
  // ---------------------------------------------
  renderView = () => (
    <div style={{ position: 'relative' }}>
      <img
        style={{ width: '100%' }}
        src={this.state.signature.url}
        alt="signature"
      />
      <Delete
        style={{
          position: 'absolute',
          right: '8px',
          bottom: '8px',
          cursor: 'pointer'
        }}
        onClick={this.deleteSignature}
      />
    </div>
  );

  // Update Canvas Size
  // ---------------------------------------------
  updateCanvasSize = dimensions => {
    // Necessary to do this for high DPI devices with higher pixel ratios
    // signaturePad will attempt to resize/transform the canvas, so this is making sure
    // we always get back to where we started
    const pixelRatio = window.devicePixelRatio || 1;
    const deviceWidth = dimensions.width / pixelRatio;
    const deviceHeight = 150 / pixelRatio;

    this.setState({ width: deviceWidth, height: deviceHeight }, () => {
      // handleResize() comes from the source code for the
      // signaturePad package we are using. Check github, but basically we combine
      // this call with the Measure package to handle correct sizing of the signature
      // pad. Measure is called on initial load and whenever the div in the return of the
      // render is changed.
      this.signaturePad.handleResize();

      // handleResize does some funky transformations to the canvas, so the following
      // undoes them to make sure the canvas is the stroke is the correct size/transformation
      // and the background color is filling the entire canvas
      const ctx = this.signaturePad._canvas.getContext('2d');
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.fillStyle = '#f5f5f5';
      ctx.fillRect(0, 0, dimensions.width, 150);
    });
  };

  // Render
  // ---------------------------------------------
  render() {
    if (this.state.saving) {
      return (
        <div
          style={{
            width: '100%',
            height: '100px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            border: '1px solid #E5E2E2'
          }}
        >
          <Ripple color="#50E3C2" />
        </div>
      );
    }

    return (
      <div style={{ border: '1px solid #E5E2E2', flex: '1 1 auto' }}>
        {this.state.signature ? this.renderView() : this.renderEdit()}
      </div>
    );
  }
}

SignatureCaptureV2.propTypes = {
  localOnly: PropTypes.bool,
  signature: PropTypes.object,
  brand: PropTypes.object.isRequired,
  booking: PropTypes.object,
  afterSave: PropTypes.func,
  afterDelete: PropTypes.func
};
SignatureCaptureV2.defaultProps = {
  localOnly: false,
  signature: null,
  booking: null,
  afterSave: null,
  afterDelete: null
};

function mapStateToProps(state) {
  return { brand: state.brand };
}

export default connect(mapStateToProps)(SignatureCaptureV2);
